/**
 * One candidate provider, presented as THREE separate judgements.
 *
 * ELIGIBILITY is a gate result — pass, fail, or not verifiable.
 * OPERATIONAL FIT is a weighted score the backend withholds entirely when a
 * hard gate failed.
 * RELATIONSHIP is human history, and it is context only: the backend ships a
 * standing disclosure saying relationship facts never gate eligibility and
 * can never rescue a failed candidate.
 *
 * They are never fused into one number. There is no "AI score" in the
 * backend payload, and inventing one here would be inventing truth.
 */
import { useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  gateKeyLabel,
  parseDisqualification,
  shortlistStateDisplay,
} from "./exchangePresentation";
import {
  Ban,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
  IdCard,
  Handshake,
  Gauge,
  AlertTriangle,
  Info,
} from "lucide-react";

const MEMBERSHIP_CLASS = {
  good: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  attention: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  bad: "bg-red-500/15 text-red-400 border-red-500/25",
  unknown: "bg-muted text-muted-foreground border-border",
};

const ELIGIBILITY_STYLE = {
  pass: {
    className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    Icon: CheckCircle2,
  },
  unknown: {
    className: "bg-amber-500/20 text-amber-400 border-amber-500/40",
    Icon: HelpCircle,
  },
  fail: {
    className: "bg-red-500/25 text-red-300 border-red-500/50",
    Icon: Ban,
  },
};

const RelationshipSummary = ({ relationship }) => {
  const facts = [];
  if (relationship.quotes) {
    facts.push(
      `${relationship.quotes} quote${relationship.quotes === 1 ? "" : "s"}` +
        (relationship.selectedQuotes ? ` (${relationship.selectedQuotes} selected)` : "")
    );
  }
  if (relationship.interests) facts.push(`${relationship.interests} expression(s) of interest`);
  if (relationship.bookings) facts.push(`${relationship.bookings} booking(s)`);
  if (relationship.memberships) facts.push(`${relationship.memberships} membership(s)`);

  if (facts.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No recorded history with this provider. That is unknown history — not a mark
        against them.
      </p>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-foreground/90">{facts.join(" · ")}</p>
      {relationship.sourcesTruncated && (
        <p className="text-[10px] text-muted-foreground">
          The counts are complete; the underlying record list was too long to itemise.
        </p>
      )}
    </div>
  );
};

const ProviderMatchCard = ({ candidate, onOpenPassport, shortlistState }) => {
  const [open, setOpen] = useState(false);
  const eligibility = candidate.eligibility;
  const style = ELIGIBILITY_STYLE[eligibility.tone] || ELIGIBILITY_STYLE.unknown;
  const isFail = eligibility.status === "FAIL";
  // Membership is decided on the Shortlist tab, not here. Showing it stops a
  // PM re-entering a provider they have already judged.
  const membership = shortlistState ? shortlistStateDisplay(shortlistState) : null;

  return (
    <Card
      className={
        isFail
          ? "border-red-500/50 bg-red-500/[0.06]"
          : eligibility.status === "UNKNOWN"
            ? "border-amber-500/30"
            : "border-border"
      }
    >
      <CardContent className="pt-4 space-y-3">
        {/* Identity + the dominant eligibility verdict */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className={`text-base font-semibold ${isFail ? "text-red-300" : ""}`}>
              {candidate.title}
            </h3>
            {candidate.confidence && (
              <p className="text-[11px] text-muted-foreground">
                Data confidence: {candidate.confidence}
                {candidate.dataAgeDays !== null ? ` · ${candidate.dataAgeDays} days old` : ""}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            {membership && (
              <Badge
                variant="outline"
                className={`${MEMBERSHIP_CLASS[membership.tone] || MEMBERSHIP_CLASS.unknown} font-normal text-[10px]`}
                title="This provider's shortlist membership. Decide it on the Shortlist tab."
              >
                {membership.label}
              </Badge>
            )}
            <Badge variant="outline" className={`${style.className} font-semibold gap-1`}>
              <style.Icon className="w-3.5 h-3.5" />
              {eligibility.label}
            </Badge>
          </div>
        </div>

        {/* An ineligible provider states why, first and loudest. */}
        {isFail && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 space-y-1">
            <p className="text-xs font-semibold text-red-300 flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" />
              Fails a mandatory requirement — cannot be shortlisted
            </p>
            {eligibility.disqualifications.map((reason, i) => {
              const { gateLabel, detail } = parseDisqualification(reason);
              return (
                <p key={i} className="text-xs text-red-200/90">
                  {gateLabel && <span className="font-medium">{gateLabel}: </span>}
                  {detail}
                </p>
              );
            })}
            {eligibility.remediation.length > 0 && (
              <p className="text-[11px] text-muted-foreground pt-1">
                To become eligible: {eligibility.remediation.join(" ")}
              </p>
            )}
          </div>
        )}

        {eligibility.status === "UNKNOWN" && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" />
              Not verifiable — unverified is never a pass
            </p>
            {candidate.whyMatched && (
              <p className="text-xs text-muted-foreground">{candidate.whyMatched}</p>
            )}
            {eligibility.unverifiedGates.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Could not be checked:{" "}
                {eligibility.unverifiedGates.map((key) => gateKeyLabel(key).label).join(", ")}
              </p>
            )}
          </div>
        )}

        {eligibility.status === "PASS" && candidate.whyMatched && (
          <p className="text-xs text-emerald-400/90 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {candidate.whyMatched}
          </p>
        )}

        {/* The two remaining judgements, side by side and clearly separate */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg bg-muted/30 p-3 space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Gauge className="w-3 h-3" />
              Operational fit
            </p>
            {candidate.fit.scoreWithheld ? (
              <>
                <p className="text-sm font-semibold text-muted-foreground">Withheld</p>
                <p className="text-[11px] text-muted-foreground">{candidate.fit.explanation}</p>
              </>
            ) : candidate.fit.score === null ? (
              <p className="text-sm font-semibold text-muted-foreground">Unknown</p>
            ) : (
              <>
                <p className="text-xl font-bold">{Math.round(candidate.fit.score)}/100</p>
                <p className="text-[11px] text-muted-foreground">{candidate.fit.explanation}</p>
              </>
            )}
          </div>

          <div className="rounded-lg bg-muted/30 p-3 space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Handshake className="w-3 h-3" />
              Relationship
              {candidate.relationship.known && (
                <Badge
                  variant="outline"
                  className="bg-blue-500/15 text-blue-400 border-blue-500/25 font-normal text-[9px] ml-1"
                >
                  Known provider
                </Badge>
              )}
            </p>
            <RelationshipSummary relationship={candidate.relationship} />
            {/* The backend ships this disclosure precisely so relationship
                context cannot be read as a qualification. It belongs beside
                the relationship box, not hidden one click away. */}
            {candidate.relationship.note && (
              <p className="text-[10px] text-muted-foreground pt-0.5">
                {candidate.relationship.note}
              </p>
            )}
          </div>
        </div>

        {/* Actions. The trigger and the panel must live under ONE Collapsible
            root, or the trigger's aria-controls points at nothing. */}
        <Collapsible open={open} onOpenChange={setOpen} className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {candidate.passportOrganizationId ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => onOpenPassport?.(candidate.passportOrganizationId)}
              >
                <IdCard className="w-3.5 h-3.5 mr-1.5" />
                View passport
              </Button>
            ) : candidate.confirmedIdentityCount > 1 ? (
              // Two confirmed anchors is a real state, not an error. Opening
              // one of them would name an identity OMNΛS has not chosen.
              <span
                className="text-[11px] text-muted-foreground inline-flex items-center gap-1"
                title="This listing's vendor and subcontractor records are confirmed to different organizations. OMNΛS picks no winner between them, so no single passport can be opened from here."
              >
                <Info className="w-3 h-3" />
                {candidate.confirmedIdentityCount} confirmed identities — no single passport
              </span>
            ) : (
              <span
                className="text-[11px] text-muted-foreground inline-flex items-center gap-1"
                title="A passport is assembled from records joined by a CONFIRMED identity link. This listing has none, so there is nothing to open."
              >
                <Info className="w-3 h-3" />
                No confirmed identity — no passport
              </span>
            )}
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 text-xs ml-auto">
                Explain match
                <ChevronDown
                  className={`w-3.5 h-3.5 ml-1 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className="space-y-3 pt-1">
            {/* Gate-by-gate evidence */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Eligibility gates</p>
              {eligibility.gates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No gates applied — this package states no mandatory requirement the
                  catalog can be checked against.
                </p>
              ) : (
                eligibility.gates.map((gate, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <Badge
                      variant="outline"
                      className={`shrink-0 font-normal text-[10px] ${
                        gate.outcome === "PASS"
                          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
                          : gate.outcome === "FAIL"
                            ? "bg-red-500/15 text-red-400 border-red-500/25"
                            : "bg-amber-500/10 text-amber-400/80 border-amber-500/20"
                      }`}
                    >
                      {gateKeyLabel(gate.key).label}: {gate.outcome}
                    </Badge>
                    <span className="text-muted-foreground">{gate.detail}</span>
                  </div>
                ))
              )}
            </div>

            {/* Fit factor breakdown — shown even when the score is withheld,
                because the backend still ships it and hiding it would hide
                why the provider looked attractive before the gate failed. */}
            {candidate.fit.factors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground">
                  Operational fit factors
                  {candidate.fit.scoreWithheld && " (no score — a failed gate is never scored)"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                  {candidate.fit.factors.map((factor, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="text-muted-foreground truncate" title={factor.evidence}>
                        {String(factor.factor).replaceAll("_", " ")}
                        {factor.confidence === "UNKNOWN" && " (no data)"}
                      </span>
                      <span className="font-mono shrink-0">
                        {typeof factor.contribution === "number"
                          ? `${factor.contribution.toFixed(1)} pts`
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {candidate.missingData.length > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-400" />
                  Unknown about this provider
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {candidate.missingData.join(", ")}
                </p>
              </div>
            )}

          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default ProviderMatchCard;
